/**
 * List Component
 */

import { IBuilderComponent } from '../../base'
import { BaseAiComponentInput } from '../../registry'
import type {
  ColorAdaptiveValue,
  ColorVariant,
  ComponentAlignment,
  CornerRadiusValue,
  LimitInput,
  LimitSettings,
  ResponsiveValue,
  SpacingInputValue,
  TypographyValue,
  VariableTextValue,
} from '../../types'
import {
  deepMerge,
  parseAlignmentFormat,
  parseColorFormat,
  parseLimitFormat,
  parseResponsiveFormat,
  parseSpacingFormat,
  parseToVariableText,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import {
  formatFromVariableText,
  formatLimitFormat,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
  formatComponentAlignmentFormat,
  formatColorFormat,
  formatResponsiveValue,
  formatCornerRadiusFormat,
  formatAspectRatioFormat,
  formatOrientationFormat,
} from '../../format-utils'
import listTemplate from './list.template.json'

export interface IListComponentSettings {
  // Data settings
  'list-data-source': string
  'list-data-title': string
  'list-data-supporting-text': string
  'list-data-text-3': VariableTextValue
  'list-data-media': string
  'list-data-badge': VariableTextValue
  'list-data-trailing-metadata': any
  'list-data-comp-title': VariableTextValue
  'list-data-sort-by': string
  'list-data-sort-type': string
  'list-data-filter': any
  'list-data-set-limit': LimitSettings

  // Design settings
  'list-design-comp-title-color': any
  'list-design-comp-title-color-solid': ColorAdaptiveValue<ColorVariant>
  'list-design-comp-title-color-start-gradient': ColorAdaptiveValue<ColorVariant>
  'list-design-comp-title-color-end-gradient': ColorAdaptiveValue<ColorVariant>
  'list-design-comp-title-color-gradient-direction': any
  'list-design-width': ResponsiveValue<string[] | number[]>
  'list-design-image-width': ResponsiveValue<string[] | number[]>
  'list-design-icon-size': ResponsiveValue<string>
  'list-design-media-ratio': ResponsiveValue<any>
  'list-design-comp-h-align': ResponsiveValue<ComponentAlignment>
  'list-design-comp-title-align': ResponsiveValue<any>
  'list-item-design-shape': CornerRadiusValue
  'list-design-media-corner': any
  'list-design-orientation': ResponsiveValue<string>
  'list-design-num-cols': ResponsiveValue<any>
  'list-design-num-rows': ResponsiveValue<any>
  'list-design-pagination': ResponsiveValue<any>
  'list-design-hover-effect': ResponsiveValue<any>
  'list-design-item-bg-color': string
  'list-design-item-bg-opacity': string
  'list-design-typography': {
    'list-design-group-header-typography': ResponsiveValue<TypographyValue>
    'list-design-headline-typography': ResponsiveValue<TypographyValue>
    'list-design-label-typography': ResponsiveValue<TypographyValue>
    'list-design-metadata-typography': ResponsiveValue<TypographyValue>
    'list-design-supporting-text-typography': ResponsiveValue<TypographyValue>
    'list-design-text-3-typography': ResponsiveValue<TypographyValue>
  }
  'list-design-margin': ResponsiveValue<SpacingInputValue>
  'list-design-item-gap': ResponsiveValue<SpacingInputValue>
  'list-design-item-padding': ResponsiveValue<SpacingInputValue>

  // Behavior settings
  'list-behavior-user-search': boolean
  'list-behavior-add-button-show': boolean
}

export interface IListComponent extends IBuilderComponent {
  componentId: 'list'
  groupId: 'list'
  settings: IListComponentSettings
}

/**
 * List AI Input
 */
export interface AiListInput extends BaseAiComponentInput {
  dataSource: string // Required: Table ID
  listTitle?: string[] // Format: ["text:List Title"] or ["text:Items in ", "cr:category"]
  titleColumn: string // Column ID for item title
  supportingTextColumn?: string // Column ID for supporting text
  mediaColumn?: string // Column ID for media (images)
  sortBy?: string // Column ID to sort by
  sortType?: 'asc' | 'desc' // Sort direction
  limit?: LimitInput // Number of items to display

  // Layout
  orientation?: 'vertical' | 'horizontal' // List orientation
  columns?: number | string // Number of columns (e.g., 1, 2, 3 or "1 ta:2 de:3")
  rows?: number // Number of rows per page

  // Styling
  width?: string // Format: "w:100%" or "w:100% de:w:60%"
  alignment?: string // Format: "center" or "left"
  titleAlignment?: string // Title alignment
  margin?: string // Format: "m:16px"
  itemPadding?: string // Padding for each item
  itemGap?: string // Gap between items
  itemCornerRadius?: string // Corner radius: "none", "small", "medium", "large"
  mediaCornerRadius?: string // Media corner radius
  mediaRatio?: string // Media aspect ratio: "1:1", "16:9", "4:3"

  // Colors
  titleColor?: string // Title color
  itemBackgroundColor?: string // Item background color

  // Typography
  typography?: {
    title?: string // Title typography
    supportingText?: string // Supporting text typography
  }

  // Behavior
  showSearch?: boolean // Show search bar
  showAddButton?: boolean // Show add button
  showPagination?: boolean // Show pagination
  hoverEffect?: boolean // Enable hover effect
}

const LIST_DEFAULT_TEMPLATE = listTemplate as unknown as IListComponentSettings

export class ListComp {
  getTemplate(): IListComponentSettings {
    return LIST_DEFAULT_TEMPLATE
  }

  /**
   * format list component settings to AI list input
   */
  format(settings: IListComponentSettings): Omit<AiListInput, 'type'> {
    const result: Omit<AiListInput, 'type'> = {
      dataSource: settings['list-data-source'] || '',
      titleColumn: settings['list-data-title'] || '',
    }

    // Data - Source (required)
    if (settings['list-data-source']) {
      result.dataSource = settings['list-data-source']
    }

    // Data - Title Column (required)
    if (settings['list-data-title']) {
      result.titleColumn = settings['list-data-title']
    }

    // Data - Supporting Text Column
    if (settings['list-data-supporting-text']) {
      result.supportingTextColumn = settings['list-data-supporting-text']
    }

    // Data - Media Column
    if (settings['list-data-media']) {
      result.mediaColumn = settings['list-data-media']
    }

    // Data - Component Title
    if (settings['list-data-comp-title']) {
      result.listTitle = formatFromVariableText(settings['list-data-comp-title'])
    }

    // Data - Sort
    if (settings['list-data-sort-by']) {
      result.sortBy = settings['list-data-sort-by']
    }

    if (settings['list-data-sort-type']) {
      result.sortType = settings['list-data-sort-type'] as 'asc' | 'desc'
    }

    // Data - Limit
    if (settings['list-data-set-limit']) {
      result.limit = formatLimitFormat(settings['list-data-set-limit'])
    }

    // Layout - Orientation
    if (settings['list-design-orientation']) {
      const orientation = formatOrientationFormat(settings['list-design-orientation'])
      if (orientation === 'vertical' || orientation === 'horizontal') {
        result.orientation = orientation
      }
    }

    // Layout - Columns
    if (settings['list-design-num-cols']) {
      const allSame = Object.values(settings['list-design-num-cols']).every((v) => v === Object.values(settings['list-design-num-cols'])[0])
      if (allSame) {
        result.columns = Object.values(settings['list-design-num-cols'])[0]
      } else {
        result.columns = formatResponsiveValue(settings['list-design-num-cols'], (val) => String(val))
      }
    }

    // Layout - Rows
    if (settings['list-design-num-rows']) {
      const allSame = Object.values(settings['list-design-num-rows']).every((v) => v === Object.values(settings['list-design-num-rows'])[0])
      if (allSame) {
        const val = Object.values(settings['list-design-num-rows'])[0]
        result.rows = typeof val === 'number' ? val : Number(val)
      } else {
        // For responsive values, we'll use the phone value as default
        const phoneVal = settings['list-design-num-rows'].phone
        result.rows = typeof phoneVal === 'number' ? phoneVal : Number(phoneVal)
      }
    }

    // Design - Width
    if (settings['list-design-width']) {
      result.width = formatWidthFormat(settings['list-design-width'])
    }

    // Design - Alignment
    if (settings['list-design-comp-h-align']) {
      result.alignment = formatComponentAlignmentFormat(settings['list-design-comp-h-align'])?.replace(/^h:/, '') || undefined
    }

    // Design - Title Alignment
    if (settings['list-design-comp-title-align']) {
      const allSame = Object.values(settings['list-design-comp-title-align']).every((v) => v === Object.values(settings['list-design-comp-title-align'])[0])
      if (allSame) {
        result.titleAlignment = String(Object.values(settings['list-design-comp-title-align'])[0])
      } else {
        result.titleAlignment = formatComponentAlignmentFormat(settings['list-design-comp-title-align'] as any)?.replace(/^h:/, '') || undefined
      }
    }

    // Design - Margin
    if (settings['list-design-margin']) {
      result.margin = formatSpacingFormat(settings['list-design-margin'])
    }

    // Design - Item Padding
    if (settings['list-design-item-padding']) {
      result.itemPadding = formatSpacingFormat(settings['list-design-item-padding'])
    }

    // Design - Item Gap
    if (settings['list-design-item-gap']) {
      result.itemGap = formatSpacingFormat(settings['list-design-item-gap'])
    }

    // Design - Item Corner Radius
    if (settings['list-item-design-shape']) {
      result.itemCornerRadius = formatCornerRadiusFormat(settings['list-item-design-shape'] as any)
    }

    // Design - Media Corner Radius
    if (settings['list-design-media-corner']) {
      result.mediaCornerRadius = formatCornerRadiusFormat(settings['list-design-media-corner'] as any)
    }

    // Design - Media Ratio
    if (settings['list-design-media-ratio']) {
      result.mediaRatio = formatAspectRatioFormat(settings['list-design-media-ratio'])
    }

    // Design - Title Color
    if (settings['list-design-comp-title-color'] && settings['list-design-comp-title-color-solid']) {
      const color = formatColorFormat({
        colorType: settings['list-design-comp-title-color'] as any,
        colorSolid: settings['list-design-comp-title-color-solid'],
        colorStartGradient: settings['list-design-comp-title-color-start-gradient'],
        colorEndGradient: settings['list-design-comp-title-color-end-gradient'],
        colorGradientDirection: settings['list-design-comp-title-color-gradient-direction'],
      })
      if (color) {
        result.titleColor = color
      }
    }

    // Design - Item Background Color
    if (settings['list-design-item-bg-color']) {
      // Format as string - if it's an object with light/dark, use light value
      if (typeof settings['list-design-item-bg-color'] === 'object' && settings['list-design-item-bg-color'] !== null) {
        const bgColor = settings['list-design-item-bg-color'] as any
        if (bgColor.light) {
          result.itemBackgroundColor = bgColor.light
        } else if (bgColor.dark) {
          result.itemBackgroundColor = bgColor.dark
        } else {
          result.itemBackgroundColor = String(settings['list-design-item-bg-color'])
        }
      } else {
        result.itemBackgroundColor = String(settings['list-design-item-bg-color'])
      }
    }

    // Design - Typography
    if (settings['list-design-typography']) {
      result.typography = {}
      if (settings['list-design-typography']['list-design-headline-typography']) {
        result.typography.title = formatTypographyFormat(settings['list-design-typography']['list-design-headline-typography'])
      }
      if (settings['list-design-typography']['list-design-supporting-text-typography']) {
        result.typography.supportingText = formatTypographyFormat(settings['list-design-typography']['list-design-supporting-text-typography'])
      }
    }

    // Behavior - Search
    if (settings['list-behavior-user-search'] !== undefined) {
      result.showSearch = settings['list-behavior-user-search']
    }

    // Behavior - Add Button
    if (settings['list-behavior-add-button-show'] !== undefined) {
      result.showAddButton = settings['list-behavior-add-button-show']
    }

    // Behavior - Pagination
    if (settings['list-design-pagination']) {
      const allSame = Object.values(settings['list-design-pagination']).every((v) => v === Object.values(settings['list-design-pagination'])[0])
      if (allSame) {
        result.showPagination = Object.values(settings['list-design-pagination'])[0] as boolean
      } else {
        // For responsive values, use phone value as default
        result.showPagination = Boolean(settings['list-design-pagination'].phone)
      }
    }

    // Behavior - Hover Effect
    if (settings['list-design-hover-effect']) {
      const allSame = Object.values(settings['list-design-hover-effect']).every((v) => v === Object.values(settings['list-design-hover-effect'])[0])
      if (allSame) {
        result.hoverEffect = Object.values(settings['list-design-hover-effect'])[0] as boolean
      } else {
        // For responsive values, use phone value as default
        result.hoverEffect = Boolean(settings['list-design-hover-effect'].phone)
      }
    }

    return result
  }

  /**
   * parse AI list input to list component settings
   */
  parse(input: AiListInput): IListComponentSettings {
    const result = deepMerge(LIST_DEFAULT_TEMPLATE, {})

    // Data - Source (required)
    if (input.dataSource) {
      result['list-data-source'] = input.dataSource
    }

    // Data - Title Column (required)
    if (input.titleColumn) {
      result['list-data-title'] = input.titleColumn
    }

    // Data - Supporting Text Column
    if (input.supportingTextColumn) {
      result['list-data-supporting-text'] = input.supportingTextColumn
    }

    // Data - Media Column
    if (input.mediaColumn) {
      result['list-data-media'] = input.mediaColumn
    }

    // Data - Component Title
    if (input.listTitle) {
      result['list-data-comp-title'] = parseToVariableText(input.listTitle)
    }

    // Data - Sort
    if (input.sortBy) {
      result['list-data-sort-by'] = input.sortBy
    }

    if (input.sortType) {
      result['list-data-sort-type'] = input.sortType
    }

    // Data - Limit
    if (input.limit !== undefined) {
      const parsedLimit = parseLimitFormat(input.limit)
      if (parsedLimit) {
        result['list-data-set-limit'] = parsedLimit
      }
    }

    // Data - Rows (items per page)
    if (input.rows !== undefined) {
      result['list-design-num-rows'] = parseResponsiveFormat(input.rows.toString(), (val) => parseInt(val))
    }

    // Design - Width
    if (input.width) {
      result['list-design-width'] = parseWidthFormat(input.width)
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentParsed = parseAlignmentFormat(input.alignment)
      if (alignmentParsed.hAlign) {
        result['list-design-comp-h-align'] = alignmentParsed.hAlign
      }
    }

    if (input.titleAlignment) {
      const titleAlignParsed = parseAlignmentFormat(input.titleAlignment)
      if (titleAlignParsed.hAlign) {
        result['list-design-comp-title-align'] = titleAlignParsed.hAlign
      }
    }

    // Design - Margin
    if (input.margin) {
      result['list-design-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Item Padding
    if (input.itemPadding) {
      result['list-design-item-padding'] = parseSpacingFormat(input.itemPadding)
    }

    // Design - Item Gap
    if (input.itemGap) {
      result['list-design-item-gap'] = parseSpacingFormat(input.itemGap)
    }

    // Design - Item Shape
    if (input.itemCornerRadius) {
      result['list-item-design-shape'] = input.itemCornerRadius as CornerRadiusValue
    }

    // Design - Media Shape
    if (input.mediaCornerRadius) {
      result['list-design-media-corner'] = input.mediaCornerRadius as CornerRadiusValue
    }

    // Design - Media Ratio
    if (input.mediaRatio) {
      result['list-design-media-ratio'] = parseResponsiveFormat(input.mediaRatio, (val) => val)
    }

    // Design - Orientation
    if (input.orientation) {
      result['list-design-orientation'] = parseResponsiveFormat(input.orientation, (val) => val)
    }

    // Design - Columns
    if (input.columns !== undefined) {
      if (typeof input.columns === 'string') {
        result['list-design-num-cols'] = parseResponsiveFormat(input.columns, (val) => parseInt(val))
      } else {
        result['list-design-num-cols'] = parseResponsiveFormat(input.columns.toString(), (val) => parseInt(val))
      }
    }

    // Design - Title Color
    if (input.titleColor) {
      const colorParsed = parseColorFormat(input.titleColor)
      result['list-design-comp-title-color'] = colorParsed.colorType
      result['list-design-comp-title-color-solid'] = colorParsed.colorSolid
      result['list-design-comp-title-color-start-gradient'] = colorParsed.colorStartGradient
      result['list-design-comp-title-color-end-gradient'] = colorParsed.colorEndGradient
      result['list-design-comp-title-color-gradient-direction'] = colorParsed.colorGradientDirection
    }

    // Design - Item Background Color
    if (input.itemBackgroundColor) {
      const bgColorParsed = parseColorFormat(input.itemBackgroundColor)
      result['list-design-item-bg-color'] = bgColorParsed.colorSolid as any
    }

    // Design - Typography
    if (input.typography?.title) {
      const titleTypo = parseTypographyFormat(input.typography.title)
      if (!result['list-design-typography']) {
        result['list-design-typography'] = {} as any
      }
      result['list-design-typography']['list-design-headline-typography'] = titleTypo
    }

    if (input.typography?.supportingText) {
      const supportingTextTypo = parseTypographyFormat(input.typography.supportingText)
      if (!result['list-design-typography']) {
        result['list-design-typography'] = {} as any
      }
      result['list-design-typography']['list-design-supporting-text-typography'] = supportingTextTypo
    }

    // Behavior - Search
    if (input.showSearch !== undefined) {
      result['list-behavior-user-search'] = input.showSearch
    }

    // Behavior - Add Button
    if (input.showAddButton !== undefined) {
      result['list-behavior-add-button-show'] = input.showAddButton
    }

    // Behavior - Pagination
    if (input.showPagination !== undefined) {
      result['list-design-pagination'] = parseResponsiveFormat(input.showPagination.toString(), (val) => val === 'true')
    }

    // Behavior - Hover Effect
    if (input.hoverEffect !== undefined) {
      result['list-design-hover-effect'] = parseResponsiveFormat(input.hoverEffect.toString(), (val) => val === 'true')
    }

    return result
  }
}
